import type { CarrierEnabledScope } from '@klareospace/pharos-lib-types';

/**
 * Returns true if the carrier is enabled on at least one site within the given space.
 * Returns false if:
 * - The spaceId does not exist in enabledScopes
 * - The enabled sites count is 0:
 *   - When enabledOnAllSites is true: totalSitesInSpace is 0 or all sites are explicitly disabled
 *   - When enabledOnAllSites is false: no siteSpecifics entry is enabled (computed from carrier data alone)
 *
 * @param totalSitesInSpace - Required when enabledOnAllSites is true for that scope.
 */
export function isCarrierEnabledOnAnySiteWithinSpace(
  enabledScopes: CarrierEnabledScope[],
  spaceId: string,
  totalSitesInSpace: number,
): boolean {
  const scope = enabledScopes.find((s) => s.spaceId === spaceId);

  if (!scope) return false;

  const specifics = scope.siteSpecifics ?? [];

  if (scope.enabledOnAllSites) {
    if (totalSitesInSpace === 0) return false;
    if (specifics.length === 0) return true;
    const disabledCount = specifics.filter((ss) => ss.disabled).length;
    return disabledCount < totalSitesInSpace;
  }

  // enabledOnAllSites is false: check from carrier data alone
  return specifics.some((ss) => !ss.disabled);
}

/**
 * Returns true if the carrier is referenced on the given space — an `enabledScopes` entry exists
 * for that space, regardless of site enablement or site count. Referenced ⇒ visible in space-level
 * driver/vehicle lists; site enablement no longer gates space-level visibility (it still gates
 * site-level lists via `isCarrierEnabledOnSite` and archive/unreference hard-blocks via
 * `isCarrierEnabledOnAnySiteWithinSpace`, both of which stay strict).
 */
export function isCarrierReferencedInSpace(enabledScopes: CarrierEnabledScope[], spaceId: string): boolean {
  return enabledScopes.some((s) => s.spaceId === spaceId);
}

/**
 * Returns true if the carrier's enablement on the given space is unconditional — enabled on all
 * sites of the space with no per-site exception. Ignores `from`/`to` validity windows, consistent
 * with `isCarrierEnabledOnSite`. Used to decide whether a driver's carrier-perimeter scope
 * collapses to a single space-level scope or must be expanded into individual site-level scopes.
 */
export function isCarrierFullyEnabledOnSpace(enabledScopes: CarrierEnabledScope[], spaceId: string): boolean {
  const scope = enabledScopes.find((s) => s.spaceId === spaceId);
  if (!scope?.enabledOnAllSites) return false;

  const specifics = scope.siteSpecifics ?? [];
  return !specifics.some((ss) => ss.disabled);
}

/**
 * Returns true if the carrier is enabled on the given site within the given space.
 * Checks siteSpecifics entries for explicit per-site enablement.
 * When enabledOnAllSites is true, the site is enabled unless explicitly disabled.
 */
export function isCarrierEnabledOnSite(enabledScopes: CarrierEnabledScope[], siteId: string, spaceId: string): boolean {
  const scope = enabledScopes.find((s) => s.spaceId === spaceId);
  if (!scope) return false;

  const specifics = scope.siteSpecifics ?? [];
  const siteSpecific = specifics.find((ss) => ss.siteId === siteId);

  if (scope.enabledOnAllSites) {
    return siteSpecific?.disabled !== true;
  }

  return siteSpecific !== undefined && siteSpecific.disabled !== true;
}
